import { RedirectToSignIn, useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "@tanstack/react-router";
import { TriangleAlert } from "lucide-react";
import { useState, type ReactElement, type ReactNode } from "react";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle, Button, Skeleton } from "@ndd/ui";
import { NotProvisioned } from "@/components/not-provisioned";
import { RDList } from "@/components/v2/rd-list";
import type { RdListItem as EnrichedRdListItem } from "@/components/v2/rd-list";
import { EmptyStateCard } from "@/components/v2/empty-state-card";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { RosterLanes } from "@/components/v2/roster-lanes";
import {
  RosterScopeSelector,
  type RosterSupervisorOption,
} from "@/components/v2/roster-scope-selector";
import type { AlwaysAiUser } from "../../../shared/api-types";
import { FEATURE_FLAGS } from "../../../shared/features";
import {
  isRosterRowNotPublished,
  useRosterMetrics,
  type RosterMetricsRow,
} from "@/lib/metrics";
import { can, canReadRdWorkspace, landingPathForRole } from "@/lib/permissions";
import { dataThroughLabel } from "@/lib/roster-lanes";
import {
  ALL_RDS_SCOPE,
  RD_REGISTRY_LINK_TEXT,
  parseStoredRosterScope,
  rosterScopeQueryValue,
  rosterScopeStorageKey,
  rosterSubtitle,
  serializeRosterScope,
  unassignedNotice,
  type RosterScope,
} from "@/lib/roster-scope-state";
import { RD_HOME_FLAG, useFeatureEnabled, useFeatures } from "@/lib/use-features";
import {
  THEMED_BAND,
  THEMED_CARD,
  THEMED_EYEBROW,
  THEMED_PAGE_TITLE,
  THEMED_RULE,
} from "@/lib/always-look";
import { cn } from "@/lib/utils";

// Loose projection kept for consumers that predate the enriched list payload
// (rd-detail.tsx extends this shape). The RD list itself renders the enriched
// legacy shape exported from components/v2/rd-list.
export interface RdListItem {
  id: string;
  slug: string;
  name: string;
  sessionCount?: number;
  supervisorName?: string | null;
}

function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const body = error.body as { error?: string } | undefined;
    if (body?.error) return body.error;
  }

  return "Something went wrong. Please try again.";
}

export function RdsWorkspace(): ReactElement | null {
  const { getToken, isLoaded, isSignedIn } = useAuth({
    treatPendingAsSignedOut: false,
  });
  const user = useQuery({
    queryKey: ["current-user"],
    enabled: isLoaded && isSignedIn,
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
  const currentUser = user.data?.user;
  // Defense in depth: WorkspaceShell now bounces non-workspace roles off
  // /rds before this renders, and its flag query warms this cache.
  const rdHomeEnabled = useFeatureEnabled(RD_HOME_FLAG);

  if (isLoaded && !isSignedIn) return <RedirectToSignIn />;

  if (user.isError) {
    if (user.error instanceof ApiRequestError && user.error.status === 403) {
      return <NotProvisioned />;
    }

    return <CenteredMessage message={apiErrorMessage(user.error)} />;
  }

  if (!currentUser) return null;

  if (!canReadRdWorkspace(currentUser)) {
    return <Navigate to={landingPathForRole(currentUser, rdHomeEnabled)} />;
  }

  // Whole-roster viewers pick a scope (NO-506), so their list is fetched per
  // scope in AdminRoster rather than here.
  if (can(currentUser, "rds:read:all"))
    return <AdminRoster currentUser={currentUser} />;

  return <SupervisorRoster currentUser={currentUser} />;
}

function CenteredMessage({ message }: { message: string }): ReactElement {
  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
}

// --- Roster view (NO-559) ---------------------------------------------------

type RosterView =
  /** Metrics are ready: render lanes; the legacy roster query stays idle. */
  | {
      mode: "lanes";
      rows: RosterMetricsRow[];
      legacyEnabled: false;
      metricsRetry: null;
    }
  /** Flag confirmed on and metrics still loading: skeleton, legacy idle. */
  | { mode: "skeleton"; rows: undefined; legacyEnabled: false; metricsRetry: null }
  /** Flag off, features pending, or metrics failed: today's table (with a retry notice on failure). */
  | {
      mode: "legacy";
      rows: undefined;
      legacyEnabled: true;
      metricsRetry: (() => void) | null;
    };

/**
 * Folds the metrics state and the flag into one decision. The legacy roster
 * query is skipped only once metrics are ready or loading with the flag
 * confirmed on, so flag-off and features-pending paths behave exactly as
 * before this ticket.
 */
function useRosterView(scope: RosterScope): RosterView {
  const metrics = useRosterMetrics(scope);
  const features = useFeatures();
  const flagConfirmedOn = features.data?.data[FEATURE_FLAGS.supervisorMetrics] === true;

  if (metrics.status === "ready") {
    return {
      mode: "lanes",
      rows: metrics.data,
      legacyEnabled: false,
      metricsRetry: null,
    };
  }

  if (metrics.status === "loading" && flagConfirmedOn) {
    return {
      mode: "skeleton",
      rows: undefined,
      legacyEnabled: false,
      metricsRetry: null,
    };
  }

  const metricsRetry =
    metrics.status === "error" ? (metrics.retry ?? (() => undefined)) : null;

  return { mode: "legacy", rows: undefined, legacyEnabled: true, metricsRetry };
}

function rosterDataThrough(rows: RosterMetricsRow[]): string | null {
  // An unpublished row carries no batch, so the cutoff comes from the first row
  // the latest publication does cover.
  for (const row of rows) {
    if (!isRosterRowNotPublished(row)) return row.metrics.dataThrough;
  }

  return null;
}

function throughSuffix(rows: RosterMetricsRow[]): string {
  const dataThrough = rosterDataThrough(rows);

  return dataThrough ? ` · through ${dataThroughLabel(dataThrough)}` : "";
}

function MetricsUnavailableNotice({ onRetry }: { onRetry: () => void }): ReactElement {
  return (
    <Alert variant="warning" className="mb-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <TriangleAlert />
        <AlertTitle className="mb-0">Metrics unavailable</AlertTitle>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </Alert>
  );
}

function RosterPage({
  heading,
  subtitle,
  children,
}: {
  heading: ReactNode;
  subtitle: string | null;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="max-w-[1344px] mx-auto px-6 py-8 brand:max-w-[1080px]">
      <BreadcrumbSetter segments={[{ label: "RDs" }]} />
      {/* Page header: stays mounted across scope and view changes */}
      <div className="flex items-center justify-between mb-8">
        <div>
          {heading}
          {subtitle !== null && (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

// --- Supervisor roster ------------------------------------------------------

const RDS_HEADING = (
  <h1 className={cn("text-3xl font-bold font-sans text-foreground", THEMED_PAGE_TITLE)}>
    RDs
  </h1>
);

/** The server scopes a supervised viewer's roster itself, so the scope is nominal. */
function SupervisorRoster({
  currentUser,
}: {
  currentUser: AlwaysAiUser;
}): ReactElement | null {
  const { getToken } = useAuth();
  const view = useRosterView(ALL_RDS_SCOPE);
  const rds = useQuery({
    queryKey: ["rds"],
    enabled: view.legacyEnabled,
    queryFn: () =>
      authenticatedApi<{ data: EnrichedRdListItem[] }>(getToken, "/api/rds"),
  });

  if (view.mode === "skeleton") {
    return (
      <RosterPage heading={RDS_HEADING} subtitle={null}>
        <RosterSkeleton />
      </RosterPage>
    );
  }

  if (view.mode === "legacy" && rds.isError) {
    return <CenteredMessage message={apiErrorMessage(rds.error)} />;
  }

  const rows = view.mode === "lanes" ? view.rows : rds.data?.data;

  if (rows === undefined) {
    // Legacy pending. After a metrics failure the notice shows at once and the
    // table follows one round trip later.
    if (view.metricsRetry === null) return null;

    return (
      <RosterPage heading={RDS_HEADING} subtitle={null}>
        <MetricsUnavailableNotice onRetry={view.metricsRetry} />
        <RosterSkeleton />
      </RosterPage>
    );
  }

  if (rows.length === 0) {
    return (
      <>
        <BreadcrumbSetter segments={[{ label: "RDs" }]} />
        <EmptyStateCard userName={currentUser.name} />
      </>
    );
  }

  return <SupervisorRosterBody view={view} rows={rows} />;
}

function SupervisorRosterBody({
  view,
  rows,
}: {
  view: RosterView;
  rows: EnrichedRdListItem[];
}): ReactElement {
  if (view.mode === "lanes") {
    return (
      <RosterPage
        heading={RDS_HEADING}
        subtitle={`${rows.length} on your roster${throughSuffix(view.rows)}`}
      >
        <RosterLanes rows={view.rows} />
      </RosterPage>
    );
  }

  return (
    <RosterPage
      heading={RDS_HEADING}
      subtitle={`${rows.length} registered dietitian${rows.length !== 1 ? "s" : ""}`}
    >
      {view.metricsRetry && <MetricsUnavailableNotice onRetry={view.metricsRetry} />}
      <RDList rds={rows} />
    </RosterPage>
  );
}

// --- Admin roster (NO-506) --------------------------------------------------

function readStoredRosterScope(userId: string): string | null {
  try {
    return window.localStorage.getItem(rosterScopeStorageKey(userId));
  } catch {
    return null;
  }
}

function writeStoredRosterScope(userId: string, scope: RosterScope): void {
  try {
    window.localStorage.setItem(
      rosterScopeStorageKey(userId),
      serializeRosterScope(scope)
    );
  } catch {
    // Storage can be unavailable (private mode); the session state still works.
  }
}

/**
 * One supervisor's roster at a time. The stored choice is kept as its raw
 * string and resolved against the live supervisor list on every render, so
 * the scope is only ever a listed supervisor, All, or Unassigned — an id the
 * list no longer carries falls back to All without an effect. The roster
 * query keys on the scope value, so a switch is a fresh pending query (the
 * skeleton), never another scope's rows. The body (and its metrics query)
 * mounts only once the supervisor list has resolved, so the stored scope is
 * the first scope ever requested.
 */
function AdminRoster({ currentUser }: { currentUser: AlwaysAiUser }): ReactElement {
  const { getToken } = useAuth();
  const supervisors = useQuery({
    queryKey: ["roster-supervisors"],
    queryFn: () =>
      authenticatedApi<{
        data: { supervisors: Array<{ id: string; name: string; email: string }> };
      }>(getToken, "/api/rds?include=supervisors"),
  });
  const [storedScope, setStoredScope] = useState(() =>
    readStoredRosterScope(currentUser.id)
  );

  const supervisorOptions: RosterSupervisorOption[] | undefined =
    supervisors.data?.data.supervisors;
  const knownSupervisorIds = (supervisorOptions ?? []).map(
    (supervisor) => supervisor.id
  );
  const scope = parseStoredRosterScope(storedScope, knownSupervisorIds);

  const changeScope = (next: RosterScope): void => {
    setStoredScope(serializeRosterScope(next));
    writeStoredRosterScope(currentUser.id, next);
  };

  if (supervisors.isError) {
    return (
      <QueryErrorState
        error={supervisors.error}
        notFoundText="RDs are unavailable."
        onRetry={() => void supervisors.refetch()}
        className="h-full"
      />
    );
  }

  if (supervisorOptions === undefined) {
    return (
      <RosterPage
        heading={<Skeleton className="h-9 w-56" />}
        subtitle={rosterSubtitle({
          scope,
          rdCount: 0,
          supervisorCount: 0,
          loading: true,
        })}
      >
        <RosterSkeleton />
      </RosterPage>
    );
  }

  return (
    <AdminRosterBody
      currentUser={currentUser}
      scope={scope}
      supervisorOptions={supervisorOptions}
      onChangeScope={changeScope}
    />
  );
}

function AdminRosterBody({
  currentUser,
  scope,
  supervisorOptions,
  onChangeScope,
}: {
  currentUser: AlwaysAiUser;
  scope: RosterScope;
  supervisorOptions: RosterSupervisorOption[];
  onChangeScope: (next: RosterScope) => void;
}): ReactElement {
  const { view, roster, rows, legacyError, loading } = useAdminRosterData(scope);
  const rosterIsEmpty = rows !== undefined && rows.length === 0;
  const supervisorName =
    scope.kind === "supervisor"
      ? supervisorOptions.find((supervisor) => supervisor.id === scope.supervisorId)
          ?.name
      : undefined;

  // No RDs anywhere is still first-run; only a scoped view makes the welcome
  // card wrong.
  if (rosterIsEmpty && scope.kind === "all") {
    return (
      <>
        <BreadcrumbSetter segments={[{ label: "RDs" }]} />
        <EmptyStateCard userName={currentUser.name} />
      </>
    );
  }

  const subtitle = legacyError
    ? null
    : rosterSubtitle({
        scope,
        rdCount: rows?.length ?? 0,
        supervisorCount: supervisorOptions.length,
        loading,
      }) + (view.mode === "lanes" ? throughSuffix(view.rows) : "");

  return (
    <RosterPage
      heading={
        <RosterScopeSelector
          scope={scope}
          supervisors={supervisorOptions}
          onChange={onChangeScope}
        />
      }
      subtitle={subtitle}
    >
      {view.metricsRetry && <MetricsUnavailableNotice onRetry={view.metricsRetry} />}
      <AdminRosterContent
        view={view}
        rows={rows}
        loading={loading}
        legacyError={legacyError ? roster.error : null}
        onRetryLegacy={() => void roster.refetch()}
        scope={scope}
        supervisorName={supervisorName}
        onShowAll={() => onChangeScope(ALL_RDS_SCOPE)}
      />
    </RosterPage>
  );
}

/** The scoped legacy query beside the metrics view; only one of them is ever live. */
function useAdminRosterData(scope: RosterScope) {
  const { getToken } = useAuth();
  const scopeValue = rosterScopeQueryValue(scope);
  const view = useRosterView(scope);
  const roster = useQuery({
    queryKey: ["rds", scopeValue],
    enabled: view.legacyEnabled,
    queryFn: () =>
      authenticatedApi<{ data: EnrichedRdListItem[] }>(
        getToken,
        `/api/rds?supervisor=${encodeURIComponent(scopeValue)}`
      ),
  });
  const rows = view.mode === "lanes" ? view.rows : roster.data?.data;
  const legacyError = view.mode === "legacy" && roster.isError;
  const loading =
    view.mode === "skeleton" || (view.mode === "legacy" && roster.isPending);

  return { view, roster, rows, legacyError, loading };
}

function AdminRosterContent({
  view,
  rows,
  loading,
  legacyError,
  onRetryLegacy,
  scope,
  supervisorName,
  onShowAll,
}: {
  view: RosterView;
  rows: EnrichedRdListItem[] | undefined;
  loading: boolean;
  legacyError: unknown;
  onRetryLegacy: () => void;
  scope: RosterScope;
  supervisorName: string | undefined;
  onShowAll: () => void;
}): ReactElement {
  if (legacyError) {
    return (
      <QueryErrorState
        error={legacyError}
        notFoundText="RDs are unavailable."
        onRetry={onRetryLegacy}
        className="py-16"
      />
    );
  }

  if (loading || rows === undefined) return <RosterSkeleton />;

  if (rows.length === 0) {
    return (
      <ScopedEmptyCard
        scope={scope}
        supervisorName={supervisorName}
        onShowAll={onShowAll}
      />
    );
  }

  return (
    <>
      {scope.kind === "unassigned" && <UnassignedNotice count={rows.length} />}
      {view.mode === "lanes" ? <RosterLanes rows={view.rows} /> : <RDList rds={rows} />}
    </>
  );
}

function UnassignedNotice({ count }: { count: number }): ReactElement {
  const notice = unassignedNotice(count);
  // The copy is one testable string; the registry phrase becomes the link here.
  const [lead, tail] = notice.body.split(RD_REGISTRY_LINK_TEXT);

  return (
    <Alert variant="warning" className="mb-4">
      <TriangleAlert />
      <AlertTitle>{notice.title}</AlertTitle>
      <AlertDescription>
        {lead}
        <Link to="/admin/rds" className="font-medium underline underline-offset-2">
          {RD_REGISTRY_LINK_TEXT}
        </Link>
        {tail}
      </AlertDescription>
    </Alert>
  );
}

function ScopedEmptyCard({
  scope,
  supervisorName,
  onShowAll,
}: {
  scope: RosterScope;
  supervisorName: string | undefined;
  onShowAll: () => void;
}): ReactElement {
  const unassigned = scope.kind === "unassigned";
  const name = supervisorName ?? "This supervisor";
  const title = unassigned ? "Every RD has a supervisor" : `${name} has no RDs yet`;
  const description = unassigned
    ? "No RDs are unassigned right now."
    : `No RDs are assigned to ${name} right now. Assign RDs in the admin registry, or pick another supervisor above.`;

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-card shadow-sm px-6 py-16 flex flex-col items-center text-center gap-5",
        THEMED_CARD
      )}
    >
      <div className="flex flex-col items-center gap-2">
        <h2 className="text-xl font-semibold font-sans text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-[440px]">
          {description}
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        {!unassigned && (
          <Button asChild variant="outline">
            <Link to="/admin/rds">Go to RD registry</Link>
          </Button>
        )}
        <Button type="button" onClick={onShowAll}>
          Show all supervisors
        </Button>
      </div>
    </div>
  );
}

const SKELETON_ROW_COUNT = 6;
const ROSTER_COLUMNS = ["RD", "Sessions", "Coverage", "Last Session", "Last Activity"];
// Mirrors the RDList table frame and column header so the skeleton and the
// roster occupy the same space.
const ROSTER_GRID_COLUMNS =
  "md:grid-cols-[minmax(0,2fr)_0.7fr_minmax(0,1.5fr)_minmax(0,1.2fr)_minmax(0,1.4fr)_24px]";

function RosterSkeleton(): ReactElement {
  return (
    <div
      role="status"
      aria-label="Loading RDs"
      className={cn(
        "rounded-xl border border-border bg-card shadow-sm overflow-hidden",
        THEMED_CARD
      )}
    >
      <div
        className={cn(
          `hidden md:grid ${ROSTER_GRID_COLUMNS} gap-4 py-3 px-5 bg-muted border-b border-border`,
          THEMED_BAND
        )}
      >
        {ROSTER_COLUMNS.map((column) => (
          <div
            key={column}
            className={cn(
              "text-xs tracking-wider text-muted-foreground font-medium uppercase",
              THEMED_EYEBROW
            )}
          >
            {column}
          </div>
        ))}
        <div />
      </div>
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
        <div
          key={index}
          className={cn(
            `grid gap-3 py-4 px-5 border-b border-border ${ROSTER_GRID_COLUMNS} md:gap-4 md:items-center`,
            THEMED_RULE
          )}
        >
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-4 w-36" />
          </div>
          <Skeleton className="h-4 w-8" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-40" />
          <div />
        </div>
      ))}
    </div>
  );
}
